export const cloneSurveyData = <T>(data: T): T => {
  if (data === undefined || data === null) {
    return data;
  }

  return JSON.parse(JSON.stringify(data));
};

const isSurveyFileValue = (value: unknown): value is Record<string, any> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return 'file' in value || ('name' in value && 'type' in value);
};

const isTransientSurveyFileValue = (value: Record<string, any>) => {
  const content = value.content;

  return typeof content !== 'string'
    || content.startsWith('data:')
    || content.startsWith('blob:')
    || content.startsWith('file:');
};

/**
 * Removes the browser File object and any local/base64 preview from survey
 * answers. While an upload is in flight, the last persisted file answer is
 * retained so an unrelated autosave cannot replace it with transient data.
 */
export const getPersistableSurveyData = (
  current: Record<string, any> = {},
  saved: Record<string, any> = {},
) => {
  const persistable: Record<string, any> = {};

  for (const [key, value] of Object.entries(current)) {
    if (!Array.isArray(value) || !value.some(isSurveyFileValue)) {
      persistable[key] = cloneSurveyData(value);
      continue;
    }

    const remoteFiles = value
      .filter((item) => isSurveyFileValue(item) && !isTransientSurveyFileValue(item))
      .map(({ file: _file, ...item }) => cloneSurveyData(item));
    const hasTransientFile = value.some((item) => isSurveyFileValue(item) && isTransientSurveyFileValue(item));

    if (remoteFiles.length > 0) {
      persistable[key] = remoteFiles;
    } else if (hasTransientFile && Object.prototype.hasOwnProperty.call(saved, key)) {
      persistable[key] = cloneSurveyData(saved[key]);
    } else if (!hasTransientFile) {
      persistable[key] = [];
    }
  }

  return persistable;
};

const isJsonEqual = (left: any, right: any): boolean => {
  if (Object.is(left, right)) {
    return true;
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left)
      && Array.isArray(right)
      && left.length === right.length
      && left.every((value, index) => isJsonEqual(value, right[index]));
  }

  if (left && right && typeof left === 'object' && typeof right === 'object') {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);

    return leftKeys.length === rightKeys.length
      && leftKeys.every((key) => Object.prototype.hasOwnProperty.call(right, key) && isJsonEqual(left[key], right[key]));
  }

  return false;
};

export const getJsonDiffShallow = (current: Record<string, any> = {}, saved: Record<string, any> = {}) => {
  const diff: Record<string, any> = {};

  for (const key in current) {
    if (!isJsonEqual(current[key], saved[key])) {
      diff[key] = current[key];
    }
  }

  for (const key in saved) {
    if (!Object.prototype.hasOwnProperty.call(current, key)) {
      diff[key] = null;
    }
  }

  return diff;
};

/**
 * Serializes draft writes so a partial save can never race a later partial
 * save or the final submission. A failed write does not poison the queue;
 * the next write can retry from the last confirmed server snapshot.
 */
export class SurveySaveCoordinator {
  private tail: Promise<void> = Promise.resolve();

  enqueue<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.tail.then(operation);
    this.tail = result.then(
      () => undefined,
      () => undefined,
    );

    return result;
  }

  waitForIdle(): Promise<void> {
    return this.tail;
  }
}

export const runSurveyFinalSubmission = <T>(
  coordinator: SurveySaveCoordinator,
  persistCompleteDraft: () => Promise<unknown>,
  submitFinalAnswers: () => Promise<T>,
): Promise<T> => coordinator.enqueue(async () => {
  try {
    await persistCompleteDraft();
  } catch {
    // Completion can still succeed after a transient draft-save failure. The
    // caller builds the final request from the complete in-memory draft.
  }

  return submitFinalAnswers();
});

/**
 * Applies a completed SurveyJS file change before waiting for its draft write.
 * Callers keep their upload/clear loading state active until this resolves so
 * a native WebView cannot close after the preview changes but before the
 * server-backed file value is saved in the mission draft.
 */
export const completeSurveyFileChange = async <T>(
  applyFileChange: () => void,
  persistDraft: () => Promise<T>,
): Promise<T> => {
  applyFileChange();
  return persistDraft();
};
